import type { LereToursPricingSuggestion } from '~/components/LERE/types';

import type { CommandDeparture, CommandPackage } from './types';

export type PricingSuggestionIndex = {
  base: Map<string, LereToursPricingSuggestion | null>;
  addedPackages: Map<string, LereToursPricingSuggestion | null>;
};

const suggestionKey = (departureId: string, day: string) => `${departureId}|${day.slice(0, 10)}`;

/** A null entry preserves ambiguity, so a later row cannot overwrite an ambiguous key. */
export function indexPricingSuggestions(suggestions: Array<LereToursPricingSuggestion>): PricingSuggestionIndex {
  const index: PricingSuggestionIndex = { base: new Map(), addedPackages: new Map() };
  for (const suggestion of suggestions) {
    const added = suggestion.package_type === 'Added package';
    const departureId = added ? suggestion.bus_id : suggestion.departure_salesforce_bk;
    if (!departureId) {
      continue;
    }
    const entries = added ? index.addedPackages : index.base;
    const key = suggestionKey(departureId, suggestion.departure_date);
    entries.set(key, entries.has(key) ? null : suggestion);
  }
  return index;
}

export function matchPricingSuggestion(
  suggestions: Array<LereToursPricingSuggestion> | PricingSuggestionIndex,
  departure: CommandDeparture,
  pkg?: CommandPackage,
) {
  const index = Array.isArray(suggestions) ? indexPricingSuggestions(suggestions) : suggestions;
  const added = pkg && pkg.type !== 'base';
  if (added && !pkg.package_opp_id) {
    return null;
  }
  const entries = added ? index.addedPackages : index.base;
  const match = entries.get(suggestionKey(departure.dep_id, departure.dep));
  if (match === null) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[TCC] Ambiguous pricing recommendation', departure.dep_id);
    }
    return null;
  }
  if (pkg?.package_opp_id && match?.offer_salesforce_bk !== pkg.package_opp_id) {
    return null;
  }
  return match ?? null;
}
export function pricingConflictMessage(message: string) {
  if (/30|stale|out.of.date|expired/i.test(message)) {
    return 'This recommendation is out of date (older than 30 hours). Refresh the recommendations.';
  }
  if (/14|wait|cooldown/i.test(message)) {
    return 'This coach is in its 14-day wait after a price change.';
  }
  if (/already|actioned/i.test(message)) {
    return 'This recommendation has already been actioned.';
  }
  return message || 'Could not action this recommendation. Refresh and try again.';
}
